// src/components/reminders/ReminderChecker.js

import { useEffect, useRef } from "react";
import { useCollection } from "../../hooks/useCollection";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import { useActivityCleaner } from "../../hooks/useActivityCleaner";
import { useAuth } from "../../hooks/useAuth";
import {
  sendProposalReminder,
  sendRequirementReminder,
  sendTaskDeadlineReminder,
  sendEventReminder,
} from "../../services/emailService";

const STORAGE_KEY = "sentReminders";
const CHECK_INTERVAL_MS = 2 * 60 * 60 * 1000;

const loadSentReminders = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
};

const saveSentReminders = (obj) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
};

const buildKey = (type, id) => `${type}__${id}`;

const ReminderChecker = () => {
  const { user } = useAuth();
  const { data: users } = useCollection("users");
  const { data: proposals } = useCollection("proposals");
  const { data: projects } = useCollection("projects");
  const { data: tasks } = useCollection("tasks");
  const { data: events } = useCollection("events");
  const { data: activities } = useCollection("activities");
  const { remove } = useAuditFirestore("tasks");
  const { cleanOldActivities } = useActivityCleaner("activities");

  const sentReminders = useRef(loadSentReminders());
  const initialized = useRef(false);
  const intervalRef = useRef(null);
  const isChecking = useRef(false);
  
  // 🆕 Referencia para saber si ya se ejecutó la limpieza de actividades
  const activitiesCleaned = useRef(false);

  const wasAlreadySent = (type, id) =>
    !!sentReminders.current[buildKey(type, id)];

  const markAsSent = (type, id) => {
    const key = buildKey(type, id);
    sentReminders.current[key] = Date.now();
    saveSentReminders(sentReminders.current);
  };

  const cleanOldEntries = () => {
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    let changed = false;
    for (const [key, ts] of Object.entries(sentReminders.current)) {
      if (typeof ts === "number" && ts < cutoff) {
        delete sentReminders.current[key];
        changed = true;
      }
    }
    if (changed) saveSentReminders(sentReminders.current);
  };

  // 🆕 NUEVO: useEffect independiente para limpieza de actividades
  useEffect(() => {
    if (!activities || activities.length === 0) {
      return;
    }

    // Evitar ejecutar la limpieza múltiples veces innecesariamente
    if (activitiesCleaned.current) {
      console.log("⏭️ La limpieza de actividades ya se ejecutó anteriormente");
      return;
    }

    const cleanActivities = async () => {
      console.log("🧹 [useEffect independiente] Iniciando limpieza de actividades...");
      console.log(`📊 Total actividades cargadas: ${activities.length}`);
      
      try {
        const result = await cleanOldActivities(activities);
        console.log("✅ [useEffect independiente] Limpieza completada:", result);
        activitiesCleaned.current = true;
      } catch (error) {
        console.error("❌ [useEffect independiente] Error en limpieza:", error);
      }
    };

    cleanActivities();
  }, [activities, cleanOldActivities]);

  // useEffect original (sin cambios en la lógica de limpieza de actividades)
  useEffect(() => {
    if (!user || !proposals || !projects || !tasks || !events || !users) return;

    const cleanOldCompletedTasks = async () => {
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

      const toClean = tasks.filter((task) => {
        if (task.status !== "completed" && task.status !== "cancelled")
          return false;
        if (task.isActive === false) return false;
        const completedDate = task.completedAt || task.updatedAt;
        if (!completedDate) return false;
        return new Date(completedDate) <= sevenDaysAgo;
      });

      let cleaned = 0;
      let errors = 0;

      for (const task of toClean) {
        try {
          await remove(task.id);
          cleaned++;
          console.log(`🧹 Tarea inactivada: "${task.title}" (${task.id})`);
        } catch (err) {
          errors++;
          console.error(`❌ Error al inactivar tarea ${task.id}:`, err);
        }
      }

      if (cleaned > 0 || errors > 0) {
        console.log(
          `📊 Limpieza de tareas: ${cleaned} inactivadas, ${errors} errores`,
        );
      }
    };

    const sendEventReminders = async (event, usersList, reminderType) => {
      const recipients =
        event.assignedTo?.length > 0
          ? event.assignedTo
              .map((uid) =>
                usersList.find((u) => u.uid === uid || u.id === uid),
              )
              .filter((u) => u?.email)
          : [
              usersList.find(
                (u) => u.uid === event.createdBy || u.id === event.createdBy,
              ) || user,
            ];

      for (const recipient of recipients) {
        if (recipient?.email) {
          await sendEventReminder(event, recipient, reminderType);
          console.log(
            `✅ Evento (${reminderType}) → ${recipient.email}: ${event.title}`,
          );
        }
      }
    };

    const checkReminders = async () => {
      if (isChecking.current) {
        console.log("⏭️ Ya hay una comprobación en curso, omitiendo...");
        return;
      }

      isChecking.current = true;

      try {
        console.log(
          "🔔 Verificando recordatorios...",
          new Date().toLocaleTimeString(),
        );

        cleanOldEntries();

        // Solo limpiar tareas, la limpieza de actividades la hace el otro useEffect
        await cleanOldCompletedTasks();

        const now = new Date();
        const today = now.toDateString();

        // 1. PROPUESTAS
        for (const proposal of proposals) {
          if (!proposal.isActive || !proposal.reminderDate) continue;

          const reminderDate = new Date(proposal.reminderDate);
          if (
            reminderDate.toDateString() === today &&
            !wasAlreadySent("proposal", proposal.id)
          ) {
            const creator =
              users.find(
                (u) =>
                  u.uid === proposal.createdBy || u.id === proposal.createdBy,
              ) || user;
            if (creator?.email) {
              await sendProposalReminder(proposal, creator);
              markAsSent("proposal", proposal.id);
              console.log("✅ Propuesta (fecha aviso):", proposal.title);
            }
          }
        }

        // 2. PROPUESTAS ENVIADAS
        for (const proposal of proposals) {
          if (!proposal.isActive || proposal.status !== "sent") continue;

          const sentDate = new Date(proposal.updatedAt || proposal.createdAt);
          const daysSince = Math.floor(
            (now - sentDate) / (1000 * 60 * 60 * 24),
          );

          if (
            daysSince >= 7 &&
            daysSince % 7 === 0 &&
            !wasAlreadySent("proposal_sent_7d", proposal.id)
          ) {
            const creator =
              users.find(
                (u) =>
                  u.uid === proposal.createdBy || u.id === proposal.createdBy,
              ) || user;
            if (creator?.email) {
              await sendProposalReminder(proposal, creator);
              markAsSent("proposal_sent_7d", proposal.id);
              console.log("✅ Propuesta (7 días):", proposal.title);
            }
          }
        }

        // 3. PROYECTOS
        for (const project of projects) {
          if (!project.isActive) continue;
          if (!project.hasRequirement || project.requirementFile) continue;

          if (!wasAlreadySent("requirement", project.id)) {
            const creator =
              users.find((u) => u.uid === project.createdBy) || user;
            if (creator?.email) {
              await sendRequirementReminder(project, creator);
              markAsSent("requirement", project.id);
              console.log("✅ Requerimiento pendiente:", project.name);
            }
          }
        }

        // 4. TAREAS
        for (const task of tasks) {
          if (!task.isActive || !task.dueDate) continue;
          if (["completed", "cancelled", "done"].includes(task.status))
            continue;

          const hoursUntilDue =
            (new Date(task.dueDate) - now) / (1000 * 60 * 60);

          if (
            hoursUntilDue <= 24 &&
            hoursUntilDue > 22 &&
            !wasAlreadySent("task_deadline", task.id)
          ) {
            const assignedUser = task.assignedTo
              ? users.find(
                  (u) => u.uid === task.assignedTo || u.id === task.assignedTo,
                )
              : null;

            const recipient = assignedUser || user;

            if (recipient?.email) {
              await sendTaskDeadlineReminder(task, recipient);
              markAsSent("task_deadline", task.id);
              console.log(
                `✅ Tarea (único recordatorio): "${task.title}" — vence en ${Math.round(hoursUntilDue)} h`,
              );
            }
          }
        }

        // 5. EVENTOS DEL CALENDARIO
        for (const event of events) {
          if (
            !event.isActive ||
            !event.startDate ||
            event.status === "cancelled"
          )
            continue;

          const hoursUntilEvent =
            (new Date(event.startDate) - now) / (1000 * 60 * 60);

          if (
            hoursUntilEvent <= 24 &&
            hoursUntilEvent > 22 &&
            !wasAlreadySent("event_24h", event.id)
          ) {
            await sendEventReminders(event, users, "24h");
            markAsSent("event_24h", event.id);
          }

          if (
            hoursUntilEvent <= 1 &&
            hoursUntilEvent > 0.5 &&
            !wasAlreadySent("event_1h", event.id)
          ) {
            await sendEventReminders(event, users, "1h");
            markAsSent("event_1h", event.id);
          }
        }

        console.log("✅ Verificación de recordatorios completada");
      } catch (error) {
        console.error("❌ Error en checkReminders:", error);
      } finally {
        isChecking.current = false;
      }
    };

    const setupReminders = async () => {
      if (!initialized.current) {
        initialized.current = true;
        console.log("🚀 Primera ejecución de recordatorios y limpieza");
        await checkReminders();
      }

      if (intervalRef.current) {
        console.log("🧹 Limpiando intervalo anterior");
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      console.log(
        `⏰ Nuevo intervalo configurado (cada ${CHECK_INTERVAL_MS / 1000 / 60} minutos)`,
      );
      intervalRef.current = setInterval(checkReminders, CHECK_INTERVAL_MS);
    };

    setupReminders();

    return () => {
      if (intervalRef.current) {
        console.log("🧹 Cleanup final: limpiando intervalo");
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [user, proposals, projects, tasks, events, users, remove]);

  return null;
};

export default ReminderChecker;